import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Loader from '../shared/Loader';
import styles from './LoginForm.module.scss';
import { EyeIcon, EyeOffIcon } from '../icons';
import { useNotification } from '../shared/NotificationSystem';
import { useAuth } from '../../contexts/AuthContext';

export const LoginForm: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { login, authenticated, loading, error: authError } = useAuth();
    const { t } = useTranslation();
    const { showError } = useNotification();

    // Отображаем ошибку авторизации
    React.useEffect(() => {
        if (authError) {
            showError(authError);
        }
    }, [authError, showError]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const result = await login(email, password);
            if (!result) {
                showError(t('errors.loginFailed'));
            }
        } catch (error: any) {
            console.error('Ошибка при авторизации:', error);
            showError(error.message || t('errors.loginFailed'));
        }
    };

    // Функция для переключения видимости пароля
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    // Если проверка еще идет, показываем индикатор загрузки
    if (loading) {
        return <Loader />;
    }

    // Если пользователь авторизован, перенаправляем на контент
    if (authenticated) {
        return <Navigate to="/content" replace />;
    }

    return (
        <div className={styles.loginContainer}>
            <div className={styles.formBox}>
                <h1 className={styles.title}>
                    {t('login.title')}
                </h1>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <input
                        className={styles.inputField}
                        type="email"
                        required
                        placeholder={t('common.email')}
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                    />
                    <div className={styles.passwordInputContainer}>
                        <input
                            className={styles.inputField}
                            type={showPassword ? "text" : "password"}
                            required
                            placeholder={t('common.password')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                        />
                        <button
                            type="button"
                            className={styles.togglePasswordButton}
                            onClick={togglePasswordVisibility}
                            aria-label={showPassword ? t('common.hidePassword') : t('common.showPassword')}
                            tabIndex={-1}
                        >
                            {!showPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                    </div>
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={loading}
                    >
                        <span className={styles.buttonContent}>
                            {loading ? <Loader fullScreen={false} /> : t('login.loginButton')}
                        </span>
                    </button>
                </form>
            </div>
        </div>
    );
};

export default LoginForm;